import { cn } from "@/utils";
import { FileUp, ImagePlus, X } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { Alert } from "./Alert";
import { Button } from "./Button";
import { ProgressBar } from "./ProgressBar";
import { Text } from "./Text";

/**
 * Área de envio de arquivo do Design System.
 *
 * Nasceu de **cinco** cópias feitas à mão (importação de planilha, campanha,
 * imagem do topo da campanha, modelo de planilha e campanha por modelo) e de um
 * `ImageUploader` legado. Nenhuma delas era alcançável pelo teclado: a área
 * tracejada era uma `div` com `onClick`, e `div` não recebe foco nem responde ao
 * Enter.
 *
 * Aqui o caminho garantido é o gatilho: um `Button` do Design System que é, por
 * dentro, o `<label>` do `<input type="file">` nativo — o input fica escondido
 * mas **focável**, então Tab chega nele e Enter abre o seletor do sistema.
 * **Arrastar e soltar é atalho**, nunca o único caminho: no celular, onde o
 * lojista trabalha, arrastar arquivo não existe.
 *
 * Recusa de arquivo vira `Alert` **fixo na área**, dizendo o que houve e o que
 * fazer. Antes era um toast, que sumia em segundos enquanto o usuário ainda
 * procurava na tela por que nada aconteceu.
 */

/** "1,4 MB", "820 KB" — a unidade que o usuário conta, em pt-BR. */
const formatarTamanho = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;

  const kb = bytes / 1024;
  if (kb < 1024)
    return `${kb.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} KB`;

  return `${(kb / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
};

/** As fichas de `accept`, limpas e em caixa baixa: ".csv", "image/png", "image/*". */
const fichasDoAccept = (accept: string): string[] =>
  accept
    .split(",")
    .map(ficha => ficha.trim().toLowerCase())
    .filter(Boolean);

/**
 * O mesmo critério do navegador — e por isso o `accept` também vale como
 * validação de código, não só como filtro da janela do sistema: extensão,
 * família de tipo (`image/*`) ou tipo exato.
 *
 * Aceitar por tipo **ou** por extensão não é detalhe: um recorte feito no
 * navegador chega como "blob", sem extensão nenhuma, e é um arquivo válido.
 */
const arquivoAceito = (arquivo: File, accept: string): boolean => {
  const fichas = fichasDoAccept(accept);
  if (fichas.length === 0) return true;

  const nome = arquivo.name.toLowerCase();
  const tipo = arquivo.type.toLowerCase();

  return fichas.some(ficha => {
    if (ficha.startsWith(".")) return nome.endsWith(ficha);
    if (ficha.endsWith("/*")) return tipo.startsWith(ficha.slice(0, -1));
    return tipo === ficha;
  });
};

/** "CSV, XLS ou XLSX" — para o aviso dizer o que fazer, não só o que houve. */
const extensoesEscritas = (accept: string): string | null => {
  const extensoes = fichasDoAccept(accept)
    .filter(ficha => ficha.startsWith("."))
    .map(ficha => ficha.slice(1).toUpperCase());

  if (extensoes.length === 0) return null;
  if (extensoes.length === 1) return extensoes[0];

  return `${extensoes.slice(0, -1).join(", ")} ou ${extensoes[extensoes.length - 1]}`;
};

const pareceImagem = (accept: string): boolean =>
  fichasDoAccept(accept).some(
    ficha =>
      ficha.startsWith("image/") ||
      [".png", ".jpg", ".jpeg", ".webp", ".gif"].includes(ficha)
  );

export interface FileUploadProps {
  /**
   * O que se envia aqui ("Planilha de cobrança", "Imagem do topo da mensagem").
   * Vira o rótulo visível e o nome acessível do campo.
   */
  label: string;
  /** Esconde o rótulo visualmente — ele continua valendo para o leitor de tela. */
  hideLabel?: boolean;
  /** Explicação abaixo do rótulo: o que é o arquivo, de onde ele sai. */
  hint?: ReactNode;
  /**
   * Extensões e/ou tipos aceitos, como no atributo do HTML
   * (`".csv,.xls,.xlsx"`, `"image/jpeg,image/png"`).
   *
   * Vale as duas coisas: vira o filtro da janela do sistema **e** a validação
   * feita em código, porque o atributo sozinho não impede um arquivo solto na
   * área nem o "todos os arquivos" da janela.
   */
  accept: string;
  /** Tamanho máximo em bytes. Verificado em código, com aviso na própria área. */
  maxSize?: number;
  /** Arquivo escolhido. O componente é controlado pela tela. */
  file?: File | null;
  /**
   * Nome mostrado no lugar de `file.name` — para a tela que precisa mascarar
   * dado sensível antes de pôr o nome do arquivo na frente do lojista.
   */
  fileName?: string | null;
  /** Só recebe arquivo aceito: a recusa é tratada e explicada aqui dentro. */
  onSelect: (file: File) => void;
  /** Passando esta função, aparece o botão "Remover arquivo". */
  onClear?: () => void;
  /** Rótulo do gatilho quando não há arquivo. Verbo + objeto. */
  triggerLabel?: string;
  /** Rótulo do gatilho quando já há arquivo. Padrão: "Trocar arquivo". */
  replaceLabel?: string;
  /** A linha de arrastar, visível só a partir de 640px. */
  dropLabel?: string;
  /** Mostra a prévia da imagem. Padrão: ligado quando `accept` é de imagem. */
  showPreview?: boolean;
  /** Imagem já salva no servidor, para a prévia antes de qualquer escolha. */
  previewUrl?: string | null;
  /** Descreve a imagem para quem não a vê. Padrão: "Prévia de <arquivo>". */
  previewAlt?: string;
  /** Envio em andamento: bloqueia a troca e mostra o progresso. */
  isUploading?: boolean;
  /**
   * Quanto do envio já foi, de 0 a 100. Sem ele, o envio aparece como aviso de
   * andamento — barra sem número seria uma animação que não informa nada.
   */
  progress?: number;
  /** Texto do andamento. Padrão: "Enviando arquivo…". */
  uploadingLabel?: string;
  /** Erro vindo da tela (falha do envio, recusa do servidor). */
  error?: string | null;
  /** Fecha o aviso — vale para o erro da tela e para o da validação. */
  onErrorDismiss?: () => void;
  /** Substitui o aviso de tipo recusado, quando a tela sabe explicar melhor. */
  invalidTypeMessage?: string;
  /** Substitui o aviso de tamanho excedido. */
  invalidSizeMessage?: string;
  disabled?: boolean;
  className?: string;
}

export const FileUpload = ({
  label,
  hideLabel = false,
  hint,
  accept,
  maxSize,
  file = null,
  fileName,
  onSelect,
  onClear,
  triggerLabel,
  replaceLabel,
  dropLabel,
  showPreview,
  previewUrl = null,
  previewAlt,
  isUploading = false,
  progress,
  uploadingLabel = "Enviando arquivo…",
  error = null,
  onErrorDismiss,
  invalidTypeMessage,
  invalidSizeMessage,
  disabled = false,
  className,
}: FileUploadProps) => {
  const inputId = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [arrastando, setArrastando] = useState(false);
  const [erroInterno, setErroInterno] = useState<string | null>(null);
  const [previaLocal, setPreviaLocal] = useState<string | null>(null);

  /**
   * O `dragleave` dispara também quando o ponteiro passa de um filho para
   * outro dentro da área. Contando as entradas e as saídas, a moldura só apaga
   * quando o ponteiro sai de verdade — sem isso ela pisca a cada texto e botão
   * por onde o arquivo passa.
   */
  const profundidade = useRef(0);

  // Sem zerar o campo nativo, escolher o MESMO arquivo de novo depois de um
  // envio (ou de uma recusa) não dispara `change`: o navegador entende que nada
  // mudou, e o usuário fica achando que o clique não funcionou.
  useEffect(() => {
    if (!file && inputRef.current) inputRef.current.value = "";
  }, [file]);

  const ehImagem = showPreview ?? pareceImagem(accept);

  // A prévia local vive num objectURL, revogado na troca e na saída da tela —
  // sem isso cada troca de arquivo vaza um blob na memória.
  useEffect(() => {
    if (!file || !ehImagem) {
      setPreviaLocal(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviaLocal(url);

    return () => URL.revokeObjectURL(url);
  }, [file, ehImagem]);

  const receber = (escolhido: File) => {
    if (!arquivoAceito(escolhido, accept)) {
      const escritas = extensoesEscritas(accept);
      setErroInterno(
        invalidTypeMessage ??
          (escritas
            ? `Este arquivo não é aceito. Escolha um arquivo ${escritas}.`
            : "Este arquivo não é aceito. Escolha um arquivo de outro formato.")
      );
      return;
    }

    if (maxSize !== undefined && escolhido.size > maxSize) {
      setErroInterno(
        invalidSizeMessage ??
          `Este arquivo tem ${formatarTamanho(escolhido.size)} e o limite é ${formatarTamanho(maxSize)}. Escolha um arquivo menor.`
      );
      return;
    }

    setErroInterno(null);
    onSelect(escolhido);
  };

  const bloqueado = disabled || isUploading;

  const aoSoltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault();
    profundidade.current = 0;
    setArrastando(false);
    if (bloqueado) return;

    const solto = evento.dataTransfer.files[0];
    if (solto) receber(solto);
  };

  const aviso = error ?? erroInterno;
  const nomeVisivel = fileName ?? file?.name ?? null;
  const previa = previaLocal ?? previewUrl;
  const rotuloDoGatilho = file
    ? (replaceLabel ?? "Trocar arquivo")
    : (triggerLabel ?? "Escolher arquivo");

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={inputId}
          className={cn(
            "font-ds-sans text-ds-small text-ds-ink-700 font-medium",
            hideLabel && "sr-only"
          )}
        >
          {label}
        </label>
        {hint ? (
          <Text as="span" id={hintId} size="small" tone="subtle">
            {hint}
          </Text>
        ) : null}
      </div>

      <div
        onDragEnter={evento => {
          evento.preventDefault();
          profundidade.current += 1;
          if (!bloqueado) setArrastando(true);
        }}
        onDragOver={evento => {
          // preventDefault é o que impede o navegador de abrir o arquivo numa
          // aba nova; sem ele o drop nem chega aqui.
          evento.preventDefault();
        }}
        onDragLeave={() => {
          profundidade.current = Math.max(profundidade.current - 1, 0);
          if (profundidade.current === 0) setArrastando(false);
        }}
        onDrop={aoSoltar}
        className={cn(
          "rounded-ds-lg ease-ds-out flex w-full flex-col items-center gap-3 border-2 border-dashed p-4 text-center transition-colors duration-150 motion-reduce:transition-none sm:p-6",
          bloqueado
            ? "border-ds-border bg-ds-surface-sunken"
            : arrastando
              ? "border-ds-brand-500 bg-ds-brand-50"
              : "border-ds-border-strong bg-ds-surface"
        )}
      >
        {/* Arrastar não existe no toque: a linha só aparece onde há ponteiro. */}
        <Text size="small" tone="subtle" className="hidden sm:block">
          {dropLabel ?? "Arraste o arquivo aqui"}
        </Text>

        {/* O input fica escondido mas focável (`sr-only`, nunca `hidden`): é
            ele que recebe o Tab e abre o seletor no Enter. Quem aparece é o
            `<label>` vestido de botão, logo ao lado — daí o `peer`, que traz o
            anel de foco do input para o botão que o usuário enxerga. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          disabled={bloqueado}
          aria-describedby={hint ? hintId : undefined}
          aria-invalid={aviso ? true : undefined}
          className="peer sr-only"
          onChange={evento => {
            const escolhido = evento.target.files?.[0];
            if (escolhido) receber(escolhido);
            // Permite escolher o MESMO arquivo de novo depois de uma recusa.
            evento.target.value = "";
          }}
        />

        <Button
          asChild
          variant="secondary"
          iconLeft={ehImagem ? <ImagePlus /> : <FileUp />}
          className={cn(
            "peer-focus-visible:ring-ds-focus peer-focus-visible:ring-offset-ds-surface peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2",
            // Com o input desabilitado o clique no rótulo já não faz nada; o
            // cursor é o que avisa disso antes do clique.
            bloqueado && "hover:bg-ds-surface cursor-not-allowed opacity-55"
          )}
        >
          <label htmlFor={inputId}>{rotuloDoGatilho}</label>
        </Button>

        {file || previa ? (
          <div className="border-ds-border flex w-full flex-col items-center gap-2 border-t pt-3">
            {ehImagem && previa ? (
              <img
                src={previa}
                alt={
                  previewAlt ??
                  (nomeVisivel
                    ? `Prévia de ${nomeVisivel}`
                    : "Prévia da imagem enviada")
                }
                className="rounded-ds-sm border-ds-border max-h-32 w-auto border object-contain"
              />
            ) : null}

            {nomeVisivel ? (
              <div className="flex w-full flex-col items-center gap-0.5">
                {/* Nome de arquivo não tem espaço para quebrar: sem
                    `break-words`, "relatorio-de-cobrancas-setembro.xlsx"
                    estica a área para fora da tela em 360px. */}
                <Text size="small" weight="medium" className="break-words">
                  {nomeVisivel}
                </Text>
                {file ? (
                  <Text size="caption" tone="subtle" numeric>
                    {formatarTamanho(file.size)}
                  </Text>
                ) : null}
              </div>
            ) : null}

            {onClear ? (
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<X />}
                disabled={bloqueado}
                onClick={onClear}
              >
                Remover arquivo
              </Button>
            ) : null}
          </div>
        ) : null}

        {isUploading ? (
          progress === undefined ? (
            <Text
              size="small"
              tone="muted"
              role="status"
              aria-live="polite"
              className="w-full"
            >
              {uploadingLabel}
            </Text>
          ) : (
            <ProgressBar
              className="w-full"
              label={uploadingLabel}
              value={progress}
              hint={
                file
                  ? `${formatarTamanho((file.size * Math.min(Math.max(progress, 0), 100)) / 100)} de ${formatarTamanho(file.size)}`
                  : `${Math.round(progress)}%`
              }
              valueText={`${Math.round(progress)}% enviado`}
            />
          )
        ) : null}
      </div>

      <Alert
        tone="danger"
        open={!!aviso}
        onDismiss={() => {
          setErroInterno(null);
          onErrorDismiss?.();
        }}
        dismissLabel="Fechar aviso do arquivo"
      >
        {aviso}
      </Alert>
    </div>
  );
};

FileUpload.displayName = "FileUpload";
